import React, { useEffect, useState } from 'react';
import FontAwesomeIcon from 'src/components/Icon';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import Skeleton from 'src/components/Skeleton/Skeleton';
import NewBadge from 'src/components/NewBadge/NewBadge';
import {
  usePinnedResource,
  UsePinnedResourceReturn,
} from './CustomHooks/usePinnedResource';
import './pinned-resource-preview.scss';

interface PinnedResourcePreviewProps {
  serviceRequestId: string;
  token?: string;
  refreshKey?: number;
  onOpenNote?: () => void;
}

// Decode HTML entities (&#39;, &quot;, &amp;, …) via a detached textarea — its
// content is parsed as raw text, so this is XSS-safe and covers every entity
// without enumerating them.
const decodeHtmlEntities = (text: string): string => {
  const textarea: HTMLTextAreaElement = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

// The note body is editor HTML; convert block/line-break tags into real newlines
// before stripping remaining tags so the preview keeps the note's line structure
// (rendered with white-space: pre-line). Without this, every line runs together.
const htmlToPlainText = (html: string): string => {
  const withLineBreaks: string = html
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\s*\/\s*(p|div|li|h[1-6]|tr)\s*>/gi, '\n');

  const withoutTags: string = withLineBreaks.replace(/<[^>]*>/g, '');

  const decoded: string = decodeHtmlEntities(withoutTags).replace(/ /g, ' ');

  return decoded
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const PinnedResourcePreview = ({
  serviceRequestId,
  token,
  refreshKey,
  onOpenNote,
}: PinnedResourcePreviewProps) => {
  const {
    pinnedResource,
    loading,
    fetchPinnedResource,
    unpinResource,
  }: UsePinnedResourceReturn = usePinnedResource(token);

  const [showUnpinConfirm, setShowUnpinConfirm] = useState<boolean>(false);
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  const handleConfirmUnpin = async () => {
    await unpinResource(serviceRequestId);
    setShowUnpinConfirm(false);
  };

  const handleCancelUnpin = () => {
    setShowUnpinConfirm(false);
  };

  useEffect(() => {
    if (!serviceRequestId) {
      return;
    }

    const loadPinnedResource = async (): Promise<void> => {
      await fetchPinnedResource(serviceRequestId);
      setHasFetched(true);
    };

    loadPinnedResource();
  }, [serviceRequestId, refreshKey]);

  if (!hasFetched) {
    return (
      <div className="pinned-resource pinned-resource-skeleton" aria-busy="true">
        <Skeleton
          variant="circle"
          width="2.4rem"
          height="2.4rem"
          className="pinned-resource-skeleton-badge"
        />
        <Skeleton variant="text" width="55%" height="1.4rem" />
        <div className="pinned-resource-body">
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="90%" />
          <Skeleton variant="text" width="70%" />
        </div>
        <div className="pinned-resource-footer">
          <Skeleton variant="text" width="9rem" />
          <Skeleton variant="text" width="6rem" />
        </div>
      </div>
    );
  }

  if (!pinnedResource) {
    return (
      <div className="pinned-resource pinned-resource-empty">
        <NewBadge className="pinned-resource-new-badge" />
        <span className="pinned-resource-pin-badge">
          <FontAwesomeIcon glyph="thumbtack" weight="solid" />
        </span>
        <p className="pinned-resource-empty-title">No note pinned yet</p>
        <p className="pinned-resource-empty-copy">
          Pin a note here — like important contact details — so it stays front and
          centre for this request. One at a time. This pin is only visible to you.
        </p>
      </div>
    );
  }

  const authorName: string =
    `${pinnedResource.author?.firstName ?? ''} ${pinnedResource.author?.lastName ?? ''}`.trim();

  const noteText: string = htmlToPlainText(pinnedResource.body ?? '');

  return (
    <>
      <div className="pinned-resource pinned-resource-pinned">
        <span className="pinned-resource-pin-badge">
          <FontAwesomeIcon glyph="thumbtack" weight="solid" />
        </span>
        <button
          type="button"
          className="pinned-resource-unpin"
          aria-label="Unpin note"
          title="Unpin note"
          onClick={() => setShowUnpinConfirm(true)}
        >
          <FontAwesomeIcon glyph="times" weight="light" />
        </button>
        <p className="pinned-resource-title">
          {pinnedResource.title || 'Pinned note'}
        </p>
        <div className="pinned-resource-body">
          <p className="pinned-resource-snippet">{noteText}</p>
        </div>
        <div className="pinned-resource-footer">
          <button
            type="button"
            className="pinned-resource-open"
            onClick={() => onOpenNote?.()}
          >
            Open in Notes
          </button>
          {authorName && (
            <span className="pinned-resource-author">{authorName}</span>
          )}
        </div>
      </div>
      {showUnpinConfirm && (
        <ConfirmationModal
          title="Unpin Note"
          message="Unpin this note from the service request?"
          warningMessage="It will no longer appear here. You can re-pin any note at any time."
          confirmLabel="Unpin Note"
          cancelLabel="Cancel"
          onConfirm={handleConfirmUnpin}
          onCancel={handleCancelUnpin}
          isSubmitting={loading}
          variant="warning"
        />
      )}
    </>
  );
};

export default PinnedResourcePreview;